import {
  AccordionBody,
  AccordionHeader,
  AccordionItem,
  Spinner,
  UncontrolledAccordion,
  UncontrolledTooltip,
} from "reactstrap";
import { PROPERTIES_TO_HIDE } from "../constants";
import { getProjectSettings, removeOverrides } from "./service";
import { useEffect, useState } from "react";
import Select from "react-select";
import { OptionImage } from "../product/style";
import { Notification, CustomizationSpinnerContainer } from "../common/style";
import _ from "lodash";
import { updateProjectSettings } from "../project/service";
import { useParams, useNavigate } from "react-router-dom";
import ConfirmationModal from "../project/ConfirmationModal";
import { ReactComponent as ClearOverridesIcon } from "../assets/img/clear_overrides.svg";

const ProjectSettingsModal = ({
  setCurrentTab,
  currentRoom,
  loadRoom,
  activePropertyTab,
  isListProject,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [properties, setProperties] = useState([]);
  const [groupedProperties, setGroupedProperties] = useState([]);
  const [productOptions, setProductOptions] = useState({});
  const [notification, setNotification] = useState({ type: "", message: "" });
  const [isNotificationVisible, setIsNotificationVisible] = useState(true);
  const [changedProperty, setChangedProperty] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const params = useParams();
  const navigate = useNavigate();
  const projectId = params.projectId;
  const [removeOverrideConfirmationOpen, setRemoveOverrideConfirmationOpen] =
    useState(false);
  const [overridePropertyIdentifier, setOverridePropertyIdentifier] =
    useState("");

  const onDismissNotification = () => {
    setIsNotificationVisible(false);
  };

  const toggleOverrideConfirmation = () =>
    setRemoveOverrideConfirmationOpen(!removeOverrideConfirmationOpen);

  const fetchProjectSettings = () => {
    setIsLoading(true);

    getProjectSettings(projectId)
      .then((response) => {
        const properties = response.property_list;
        const selectedOptions = response.selected_values || {};

        setGroupedProperties(response.grouped_properties);
        setProperties(properties);
        setProductOptions(selectedOptions);
      })
      .catch((err) => {
        if (err.cause && err.cause.code === 403) {
          navigate("/login_with_otp");
        } else {
          setIsNotificationVisible(true);
          setNotification({ type: "error", message: err.error });
        }
      })
      .finally(() => setIsLoading(false));
  };

  const handleRemoveProjectOverride = (propertyIdentifier) => {
    setIsLoading(true);

    removeOverrides({
      projectId: projectId,
      propertyIdentifier: propertyIdentifier,
    })
      .then(() => {
        fetchProjectSettings();
        loadRoom(currentRoom.id);
      })
      .catch((error) => {
        setIsNotificationVisible(true);
        setNotification({ type: "error", message: error });
      })
      .finally(() => {
        toggleOverrideConfirmation();
        setIsLoading(false);
      });
  };

  const handleUpdateProjectSettings = (rootIdentifier, optionIdentifier) => {
    setIsLoading(true);

    return updateProjectSettings({
      projectId,
      identifier: rootIdentifier,
      value: optionIdentifier,
    })
      .then((response) => {
        setProductOptions(response.selected_values);

        if (response.refresh) fetchProjectSettings();

        loadRoom(currentRoom.id);
        setErrorMessage("");
        setChangedProperty("");
      })
      .catch(({ error, property }) => {
        let invalidFields = [];

        if (Array.isArray(error)) {
          if (error.includes("Width invalid")) invalidFields.push("Width");
          if (error.includes("Height invalid")) invalidFields.push("Height");
          if (error.includes("Depth invalid")) invalidFields.push("Depth");
        }
        let errorMessage = invalidFields.length
          ? `Some ${invalidFields.join(", ")} incompatible with this change`
          : error;
        setErrorMessage(errorMessage);
        setChangedProperty(property);
        if (!errorMessage) {
          setIsNotificationVisible(true);
          setNotification({ type: "error", message: error });
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    setIsLoading(true);
    fetchProjectSettings();
  }, []);

  const renderOptions = (
    rootIdentifier = "",
    options,
    is_editable,
    nestedLevels = 1
  ) => {
    if (!options.length) return null;

    return (
      <>
        {is_editable ? (
          <Select
            isDisabled={!is_editable}
            value={
              options.find(
                (option) => option.identifier === productOptions[rootIdentifier]
              ) || null
            }
            className="mt-2"
            options={options.map((option, index) => {
              option.value = option.name;
              option.label = option.image ? (
                <div key={index}>
                  {option.image && (
                    <OptionImage
                      key={index}
                      src={option.image}
                      alt={option.name}
                      className="me-2 border border-dark rounded-circle"
                    />
                  )}
                  {option.name}
                </div>
              ) : (
                option.name
              );
              option.level = nestedLevels;
              option.key = index;

              return {
                value: option.name,
                label: option.name,
                ...option,
              };
            })}
            onChange={(selectedOption) => {
              handleUpdateProjectSettings(
                rootIdentifier,
                selectedOption.identifier
              );
            }}
          />
        ) : (
          <div className="mt-1">
            {options.find(
              (option) => option.identifier === productOptions[rootIdentifier]
            )?.name || null}
          </div>
        )}
      </>
    );
  };

  return (
    <div
      className="fh col-md-4"
      style={{
        minWidth: "300px",
      }}
    >
      {notification.type && (
        <Notification
          color={notification.type === "error" ? "danger" : "success"}
          isOpen={isNotificationVisible}
          toggle={onDismissNotification}
        >
          {notification.message}
        </Notification>
      )}

      <div style={{ position: "relative" }}>
        {isLoading && (
          <CustomizationSpinnerContainer>
            <Spinner />
          </CustomizationSpinnerContainer>
        )}
        <div className={`${isLoading ? "blurred" : ""} mb-5`}>
          {activePropertyTab === "user" ? (
            Object.entries(groupedProperties).map(([header, identifiers]) => {
              const propertiesList = identifiers
                .map((identifier) =>
                  properties.find(
                    (e) => e.identifier == identifier && !e.admin_property
                  )
                )
                .filter((e) => e)
                .filter((property) =>
                  isListProject ? property.level === "project" : true
                );

              if (propertiesList.length === 0) {
                return null;
              }

              return (
                <div className="border-bottom properties-tab">
                  <UncontrolledAccordion stayOpen>
                    <AccordionItem key={header} className="padded">
                      <AccordionHeader targetId={header}>
                        <div className="d-flex flex-row-reverse">
                          <p className="m-0 fs-10px fw-bold active-dimension  text-uppercase">
                            {header}
                          </p>
                        </div>
                      </AccordionHeader>
                      <AccordionBody accordionId={header}>
                        {propertiesList.map((property, index) => {
                          if (PROPERTIES_TO_HIDE.includes(property.name))
                            return null;

                          return (
                            <div
                              key={index}
                              className={`${
                                !property.is_editable && "pointer"
                              }`}
                              id={`property_${property.identifier}`}
                            >
                              <div className="d-flex justify-content-between">
                                <p className="fs-10px m-0 d-inline fw-bold text-capitalize">
                                  {property.name}
                                </p>
                                {property.overridden && (
                                  <>
                                    <div
                                      className="me-1 clear-icon pointer"
                                      onClick={() => {
                                        toggleOverrideConfirmation(true);
                                        setOverridePropertyIdentifier(
                                          property.uniq_id
                                        );
                                      }}
                                      id={`clear_overriddes_${property.uniq_id}`}
                                    >
                                      <ClearOverridesIcon className="icon-black" />
                                    </div>
                                    <UncontrolledTooltip
                                      target={`clear_overriddes_${property.uniq_id}`}
                                      placement="bottom"
                                    >
                                      Clear all overridden values in modules
                                    </UncontrolledTooltip>
                                  </>
                                )}
                              </div>

                              {property.uniq_id === "internal_color" &&
                                property.options.length === 0 && (
                                  <div className="py-2 d-flex">
                                    <div className="text-secondary">
                                      No color added to the project.
                                    </div>
                                    <a
                                      onClick={() => setCurrentTab("materials")}
                                      href="#"
                                      className="ms-2"
                                    >
                                      Manage Colors
                                    </a>
                                  </div>
                                )}
                              {property.options.length > 0 &&
                                renderOptions(
                                  property.uniq_id,
                                  property.options,
                                  property.is_editable
                                )}
                              {property.depends_on && (
                                <UncontrolledTooltip
                                  target={`property_${property.identifier}`}
                                  placement="bottom"
                                >
                                  To enable this, enable{" "}
                                  <i>{property.depends_on}</i>
                                </UncontrolledTooltip>
                              )}
                              {property.uniq_id === changedProperty &&
                                errorMessage && (
                                  <div className="text-danger fs-10px mt-1">
                                    {errorMessage}
                                  </div>
                                )}
                            </div>
                          );
                        })}
                      </AccordionBody>
                    </AccordionItem>
                  </UncontrolledAccordion>
                </div>
              );
            })
          ) : (
            <div className="d-flex flex-column p-3 pb-5 row-gap-3">
              {properties
                .filter(
                  (property) =>
                    property.admin_property &&
                    (isListProject ? property.level === "project" : true)
                )
                .map((property, index) => {
                  if (PROPERTIES_TO_HIDE.includes(property.name)) return null;

                  return (
                    <div key={index}>
                      <div className="d-flex justify-content-between">
                        <p className="fs-10px m-0 d-inline fw-bold text-capitalize">
                          {property.name}
                        </p>
                        {property.overridden && (
                          <>
                            <div
                              className="me-1 clear-icon pointer"
                              onClick={() => {
                                toggleOverrideConfirmation(true);
                                setOverridePropertyIdentifier(property.uniq_id);
                              }}
                              id={`clear_overriddes_${property.uniq_id}`}
                            >
                              <ClearOverridesIcon className="icon-black" />
                            </div>
                            <UncontrolledTooltip
                              target={`clear_overriddes_${property.uniq_id}`}
                              placement="bottom"
                            >
                              Clear all overridden values in modules
                            </UncontrolledTooltip>
                          </>
                        )}
                      </div>

                      {property.uniq_id === "internal_color" &&
                        property.options.length === 0 && (
                          <div className="py-2 d-flex">
                            <div className="text-secondary">
                              No color added to the project.
                            </div>
                            <a
                              onClick={() => setCurrentTab("materials")}
                              href="#"
                              className="ms-2"
                            >
                              Manage Colors
                            </a>
                          </div>
                        )}
                      {property.options.length > 0 &&
                        renderOptions(
                          property.uniq_id,
                          property.options,
                          property.is_editable
                        )}
                    </div>
                  );
                })}
            </div>
          )}
        </div>
      </div>
      <ConfirmationModal
        isOpen={removeOverrideConfirmationOpen}
        toggle={toggleOverrideConfirmation}
        onConfirm={() =>
          handleRemoveProjectOverride(overridePropertyIdentifier)
        }
        message={`${"Are you sure you want to remove all overrides of this property in the whole project?"}`}
        headingText={`${"Confirm Remove Override"}`}
      />
    </div>
  );
};

export default ProjectSettingsModal;
